import { useCallback, useMemo, useState } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'
import {
  createObject,
  isNewObjectName,
  listObjects,
  searchObjects,
} from '@/core/repository/objectRepository'
import { logger } from '@/core/logging/logger'

interface ObjectPickerProps {
  value: string | null
  onChange: (objectId: string | null) => void
  onClose: () => void
}

/**
 * Searchable object list with inline creation.
 *
 * Typing a name that does not exist offers to create it, so adding a project
 * never means leaving the block you are editing. Making the user go to a
 * management screen first is how second dimensions end up unused.
 */
export function ObjectPicker({ value, onChange, onClose }: ObjectPickerProps) {
  const [query, setQuery] = useState('')
  const objects = useLiveQuery(() => listObjects(false), [], [])

  const matches = useMemo(() => searchObjects(objects, query), [objects, query])
  const canCreate = isNewObjectName(objects, query)

  const handleCreate = useCallback(() => {
    const name = query.trim()
    if (!name) return

    void createObject(name)
      .then((created) => {
        onChange(created.id)
        onClose()
      })
      .catch((err: unknown) => logger.error('db', 'Could not create object', err))
  }, [query, onChange, onClose])

  return (
    <div className="op-sheet" role="dialog" aria-label="Choose an object">
      <header className="op-head">
        <input
          className="op-search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search or create"
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
          autoFocus
        />
        <button className="op-close" onClick={onClose}>
          Cancel
        </button>
      </header>

      <div className="op-list scroll-y">
        {canCreate ? (
          <button className="op-item op-item--create" onClick={handleCreate}>
            Create &ldquo;{query.trim()}&rdquo;
          </button>
        ) : null}

        <button
          className={`op-item${value === null ? ' op-item--selected' : ''}`}
          onClick={() => {
            onChange(null)
            onClose()
          }}
        >
          No object
        </button>

        {matches.map((object) => (
          <button
            key={object.id}
            className={`op-item${value === object.id ? ' op-item--selected' : ''}`}
            onClick={() => {
              onChange(object.id)
              onClose()
            }}
          >
            {object.name}
          </button>
        ))}

        {matches.length === 0 && !canCreate ? (
          <p className="op-empty">No objects yet. Type a name to create one.</p>
        ) : null}
      </div>
    </div>
  )
}
